import { useState } from 'react';
import { Alert, Button, Paper, Stack, Typography } from '@mui/material';
import type { AgentDto, CampaignDto } from '@shared/services/apiService';
import { useAppSelector } from '@shared/store';
import { TestingAgentPopup } from '@widgets/Popup/TestingAgentPopup/TestingAgentPopup';
import CallAnalysisTab from '@pages/Admin/Campaign/ui/SidebarTabs/CallAnalysisTab';
import { AgentConfigurationChecks } from './AgentConfigurationChecks';

export const EvaluationWorkspace = ({
  campaign,
  agents,
  onDirtyChange,
  canTest,
  readOnly,
  agentsUnavailable = false,
}: {
  campaign: CampaignDto;
  agents: AgentDto[];
  onDirtyChange: (value: boolean) => void;
  canTest: boolean;
  readOnly: boolean;
  agentsUnavailable?: boolean;
}) => {
  const user = useAppSelector((state) => state.auth.authenticatedUser);
  const [selected, setSelected] = useState<AgentDto | null>(null);
  return (
    <Stack gap={3}>
      <AgentConfigurationChecks
        campaign={campaign}
        agents={agents}
        agentsUnavailable={agentsUnavailable}
      />
      <Alert severity="info">
        Test calls use the current campaign agents, provider settings, admission checks and credit
        rates. Analysis results appear in Call history and Analytics after processing.
      </Alert>
      {agents.map((agent) => (
        <Paper key={agent.id} variant="outlined" sx={{ p: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
            <Typography>{agent.name}</Typography>
            <Button disabled={!canTest || agentsUnavailable} onClick={() => setSelected(agent)}>
              Test agent
            </Button>
          </Stack>
        </Paper>
      ))}
      {!agents.length && (
        <Typography color="text.secondary">
          Assign an agent in the campaign before running an evaluation.
        </Typography>
      )}
      <CallAnalysisTab readOnly={readOnly} campaign={campaign} onDirtyChange={onDirtyChange} />
      {selected && (
        <TestingAgentPopup
          open
          onClose={() => setSelected(null)}
          agentId={selected.id}
          agentName={selected.name}
          campaignId={campaign.id}
          companyHash={user?.companyId || ''}
          squadId={campaign.squadId}
        />
      )}
    </Stack>
  );
};
